import { useState, useMemo } from "react";
import {
  Button,
  Chip,
  type ChipProps,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  SelectSection,
  Spinner,
  Textarea,
} from "@heroui/react";
import { Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import {
  useCreateShipmentChangeRequest,
  useShipmentChangeRequests,
  type ShipmentChangeRequestType,
  type ShipmentChangeRequest,
  willBeAutoApproved,
} from "@/api/use-shipment-change-request";
import { toast } from "@/hooks/use-toast";
import type { Shipment, ShipmentAddress } from "@/api.types.v2";
import { useTranslation } from "react-i18next";
import { PickupRescheduleDialog } from "./pickup-reschedule-dialog";

interface ShipmentChangeRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: Shipment;
}

interface ChangeTypeOption {
  value: ShipmentChangeRequestType;
  addressType: "FROM" | "TO";
}

const CHANGE_TYPE_OPTIONS: ChangeTypeOption[] = [
  {
    value: "RECIPIENT_DELIVERY_INSTRUCTIONS",
    addressType: "TO",
  },
  {
    value: "RECIPIENT_CONTACT_UPDATE",
    addressType: "TO",
  },
  {
    value: "RECIPIENT_ADDRESS_CORRECTION",
    addressType: "TO",
  },
  {
    value: "SENDER_PICKUP_INSTRUCTIONS",
    addressType: "FROM",
  },
  {
    value: "SENDER_CONTACT_UPDATE",
    addressType: "FROM",
  },
  {
    value: "SENDER_ADDRESS_CORRECTION",
    addressType: "FROM",
  },
  {
    value: "PICKUP_RESCHEDULE",
    addressType: "FROM",
  },
];

function getStatusIcon(status: ShipmentChangeRequest["status"]) {
  switch (status) {
    case "AUTO_APPROVED":
    case "ACTIONED":
      return <CheckCircle className="h-4 w-4 text-success" />;
    case "PENDING":
    case "ACKNOWLEDGED":
      return <Clock className="h-4 w-4 text-warning" />;
    case "CANNOT_FULFILL":
      return <XCircle className="h-4 w-4 text-danger" />;
    default:
      return <AlertCircle className="h-4 w-4 text-default-500" />;
  }
}

function getStatusChipColor(
  status: ShipmentChangeRequest["status"]
): ChipProps["color"] {
  switch (status) {
    case "AUTO_APPROVED":
    case "ACTIONED":
      return "success";
    case "PENDING":
    case "ACKNOWLEDGED":
      return "warning";
    case "CANNOT_FULFILL":
      return "danger";
    default:
      return "default";
  }
}

function formatAddress(addr?: ShipmentAddress): string {
  if (!addr) return "";
  const nested = addr.address;
  return [
    addr.addressLine1 ?? nested?.line1,
    addr.addressLine2 ?? nested?.line2,
    addr.addressCity ?? nested?.city,
    addr.addressProvince ?? nested?.province,
    addr.addressPostalCode ?? nested?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

function getContactFromAddress(addr?: ShipmentAddress) {
  if (!addr) return { name: "", phone: "", email: "", instructions: "" };
  return {
    name: addr.contactName ?? addr.name ?? "",
    phone: addr.contactPhone ?? addr.phone ?? "",
    email: addr.contactEmail ?? addr.email ?? "",
    instructions: addr.instructions ?? "",
  };
}

const TO_OPTIONS = CHANGE_TYPE_OPTIONS.filter((o) => o.addressType === "TO");
const FROM_OPTIONS = CHANGE_TYPE_OPTIONS.filter(
  (o) => o.addressType === "FROM"
);

export function ShipmentChangeRequestDialog({
  open,
  onOpenChange,
  shipment,
}: ShipmentChangeRequestDialogProps) {
  const { t } = useTranslation("app");
  const [selectedType, setSelectedType] =
    useState<ShipmentChangeRequestType | null>(null);
  const [pickupMoveOpen, setPickupMoveOpen] = useState(false);
  const canReschedulePickup = Boolean(
    shipment.pickupBookingId && shipment.status === "LABEL_READY"
  );
  const [formData, setFormData] = useState({
    instructions: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    addressLine2Correction: "",
    additionalNotes: "",
  });

  const fromAddress = shipment.addresses?.find((a) => a.type === "FROM");
  const toAddress = shipment.addresses?.find((a) => a.type === "TO");

  const selectedOption = CHANGE_TYPE_OPTIONS.find(
    (o) => o.value === selectedType
  );
  const currentAddress =
    selectedOption?.addressType === "FROM" ? fromAddress : toAddress;

  const currentContact = useMemo(
    () => getContactFromAddress(currentAddress),
    [currentAddress]
  );

  const originalValues = useMemo(() => {
    if (!currentAddress) return null;
    return {
      instructions:
        selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS"
          ? (shipment.deliveryNotes ?? "")
          : currentContact.instructions,
      contactName: currentContact.name,
      contactPhone: currentContact.phone,
      contactEmail: currentContact.email,
      unit: currentAddress.addressLine2 ?? currentAddress.address?.line2 ?? "",
    };
  }, [currentAddress, currentContact, selectedType, shipment.deliveryNotes]);

  const selectChangeType = (next: ShipmentChangeRequestType | null) => {
    const addressType = CHANGE_TYPE_OPTIONS.find(
      (o) => o.value === next
    )?.addressType;
    const address = addressType === "FROM" ? fromAddress : toAddress;
    const contact = getContactFromAddress(address);
    setFormData({
      instructions:
        next === "RECIPIENT_DELIVERY_INSTRUCTIONS"
          ? (shipment.deliveryNotes ?? "")
          : contact.instructions,
      contactName: contact.name,
      contactPhone: contact.phone,
      contactEmail: contact.email,
      addressLine2Correction:
        address?.addressLine2 ?? address?.address?.line2 ?? "",
      additionalNotes: "",
    });
    setSelectedType(next);
  };

  const { data: existingRequests, isLoading: loadingRequests } =
    useShipmentChangeRequests(open ? shipment.id : undefined);

  const createChangeRequest = useCreateShipmentChangeRequest({
    onSuccess: (data) => {
      toast({
        title: data.autoApproved
          ? t("shipmentChangeRequest.appliedTitle")
          : t("shipmentChangeRequest.submittedTitle"),
        description: data.autoApproved
          ? t("shipmentChangeRequest.appliedDescription", {
              changes: data.appliedChanges.join(", "),
            })
          : t("shipmentChangeRequest.submittedDescription"),
      });
      resetForm();
      onOpenChange(false);
    },
    onError: (error) => {
      const response = error as {
        response?: { data?: { message?: string } };
      };
      toast({
        variant: "destructive",
        title: t("shipmentChangeRequest.failedTitle"),
        description:
          response.response?.data?.message ??
          t("shipmentChangeRequest.failedDescription"),
      });
    },
  });

  const resetForm = () => {
    setSelectedType(null);
    setFormData({
      instructions: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      addressLine2Correction: "",
      additionalNotes: "",
    });
  };

  const hasChanges = useMemo(() => {
    if (!selectedType || !originalValues) return false;

    if (
      selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
      selectedType === "SENDER_PICKUP_INSTRUCTIONS"
    ) {
      return formData.instructions !== originalValues.instructions;
    }

    if (
      selectedType === "RECIPIENT_CONTACT_UPDATE" ||
      selectedType === "SENDER_CONTACT_UPDATE"
    ) {
      return (
        formData.contactName !== originalValues.contactName ||
        formData.contactPhone !== originalValues.contactPhone ||
        formData.contactEmail !== originalValues.contactEmail
      );
    }

    if (
      selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
      selectedType === "SENDER_ADDRESS_CORRECTION"
    ) {
      return formData.addressLine2Correction.trim() !== originalValues.unit;
    }

    return false;
  }, [selectedType, formData, originalValues]);

  const handleSubmit = () => {
    if (!selectedType || !hasChanges) return;

    createChangeRequest.mutate({
      shipmentId: shipment.id,
      requestTypes: [selectedType],
      deliveryInstructions:
        selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
        selectedType === "SENDER_PICKUP_INSTRUCTIONS"
          ? formData.instructions
          : undefined,
      contactPhone:
        selectedType === "RECIPIENT_CONTACT_UPDATE" ||
        selectedType === "SENDER_CONTACT_UPDATE"
          ? formData.contactPhone
          : undefined,
      contactName:
        selectedType === "RECIPIENT_CONTACT_UPDATE" ||
        selectedType === "SENDER_CONTACT_UPDATE"
          ? formData.contactName
          : undefined,
      contactEmail:
        selectedType === "RECIPIENT_CONTACT_UPDATE" ||
        selectedType === "SENDER_CONTACT_UPDATE"
          ? formData.contactEmail
          : undefined,
      addressLine2Correction:
        selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
        selectedType === "SENDER_ADDRESS_CORRECTION"
          ? formData.addressLine2Correction.trim()
          : undefined,
      additionalNotes: formData.additionalNotes || undefined,
    });
  };

  const isAutoApproved = selectedType
    ? willBeAutoApproved([selectedType])
    : false;
  const hasPendingRequests = existingRequests?.some(
    (r) =>
      selectedType &&
      (r.status === "PENDING" || r.status === "ACKNOWLEDGED") &&
      r.requestTypes.includes(selectedType)
  );

  const showInstructionsField =
    selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
    selectedType === "SENDER_PICKUP_INSTRUCTIONS";
  const showContactFields =
    selectedType === "RECIPIENT_CONTACT_UPDATE" ||
    selectedType === "SENDER_CONTACT_UPDATE";
  const showAddressField =
    selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
    selectedType === "SENDER_ADDRESS_CORRECTION";

  return (
    <>
      <Modal
        isOpen={open}
        onOpenChange={(o) => {
          if (!o) resetForm();
          onOpenChange(o);
        }}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="lg"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span>{t("shipmentChangeRequest.title")}</span>
                  {shipment.shipmentNumber && (
                    <span className="text-default-500 font-normal">
                      SHP-{shipment.shipmentNumber}
                    </span>
                  )}
                </div>
                <span className="text-small text-default-500 font-normal">
                  {t("shipmentChangeRequest.subtitle")}
                </span>
              </ModalHeader>
              <ModalBody className="gap-4">
                {loadingRequests ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : existingRequests && existingRequests.length > 0 ? (
                  <div className="space-y-2 pb-4 border-b border-default-200">
                    <p className="text-sm text-default-500">
                      {t("shipmentChangeRequest.previousRequests")}
                    </p>
                    {existingRequests.slice(0, 3).map((req) => (
                      <div
                        key={req.id}
                        className="flex items-start gap-2 p-2 rounded bg-default-50"
                      >
                        {req.status === "ACTIONED" &&
                        !req.appliedChanges?.length ? (
                          <AlertCircle className="h-4 w-4 text-warning" />
                        ) : (
                          getStatusIcon(req.status)
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Chip
                              color={
                                req.status === "ACTIONED" &&
                                !req.appliedChanges?.length
                                  ? "warning"
                                  : getStatusChipColor(req.status)
                              }
                              variant="flat"
                              size="sm"
                            >
                              {t(`shipmentChangeRequest.status.${req.status}`)}
                            </Chip>
                            <span className="text-xs text-default-500">
                              {new Date(req.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm mt-1">
                            {req.requestTypes
                              .map((type) =>
                                t(`shipmentChangeRequest.options.${type}`)
                              )
                              .join(", ")}
                          </p>
                          {req.status === "ACTIONED" &&
                            !req.appliedChanges?.length && (
                              <p className="text-xs text-warning mt-1">
                                {t(
                                  "shipmentChangeRequest.legacyActionedUnverified"
                                )}
                              </p>
                            )}
                          {req.submittedByActorType === "CARRIER" && (
                            <p className="text-xs text-default-500">
                              {t("shipmentChangeRequest.submittedByCarrier")}
                            </p>
                          )}
                          {req.addressLine2Correction !== undefined &&
                            req.addressLine2Correction !== null && (
                              <p className="text-xs mt-1">
                                {req.addressLine2Correction
                                  ? t("shipmentChangeRequest.unitCorrection", {
                                      unit: req.addressLine2Correction,
                                    })
                                  : t("shipmentChangeRequest.unitRemoved")}
                              </p>
                            )}
                          {req.adminResponse && (
                            <p className="text-xs text-default-500 mt-1">
                              {t("shipmentChangeRequest.response", {
                                response: req.adminResponse,
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Current Shipment Details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-default-50 p-3 rounded-md space-y-1">
                    <p className="font-medium text-default-500">
                      {t("shipmentChangeRequest.sender")}
                    </p>
                    {getContactFromAddress(fromAddress).name && (
                      <p>{getContactFromAddress(fromAddress).name}</p>
                    )}
                    {getContactFromAddress(fromAddress).phone && (
                      <p className="text-default-500">
                        {getContactFromAddress(fromAddress).phone}
                      </p>
                    )}
                    <p className="text-default-500 text-xs">
                      {formatAddress(fromAddress)}
                    </p>
                    {getContactFromAddress(fromAddress).instructions && (
                      <p className="text-default-500 text-xs italic">
                        &ldquo;{getContactFromAddress(fromAddress).instructions}
                        &rdquo;
                      </p>
                    )}
                  </div>
                  <div className="bg-default-50 p-3 rounded-md space-y-1">
                    <p className="font-medium text-default-500">
                      {t("shipmentChangeRequest.recipient")}
                    </p>
                    {getContactFromAddress(toAddress).name && (
                      <p>{getContactFromAddress(toAddress).name}</p>
                    )}
                    {getContactFromAddress(toAddress).phone && (
                      <p className="text-default-500">
                        {getContactFromAddress(toAddress).phone}
                      </p>
                    )}
                    <p className="text-default-500 text-xs">
                      {formatAddress(toAddress)}
                    </p>
                    {shipment.deliveryNotes && (
                      <p className="text-default-500 text-xs italic">
                        &ldquo;{shipment.deliveryNotes}&rdquo;
                      </p>
                    )}
                  </div>
                </div>

                {/* Change Type Selection */}
                <Select
                  label={t("shipmentChangeRequest.changeLabel")}
                  placeholder={t("shipmentChangeRequest.changePlaceholder")}
                  variant="flat"
                  selectedKeys={selectedType ? [selectedType] : []}
                  onSelectionChange={(keys) => {
                    const next = Array.from(keys)[0] as
                      | ShipmentChangeRequestType
                      | undefined;
                    if (next === "PICKUP_RESCHEDULE") {
                      setPickupMoveOpen(true);
                      onOpenChange(false);
                      return;
                    }
                    selectChangeType(next ?? null);
                  }}
                >
                  <SelectSection
                    title={t("shipmentChangeRequest.recipientSection")}
                  >
                    {TO_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        description={
                          willBeAutoApproved([option.value])
                            ? t(
                                "shipmentChangeRequest.descriptions.autoApproved"
                              )
                            : t(
                                "shipmentChangeRequest.descriptions.requiresReview"
                              )
                        }
                      >
                        {t(`shipmentChangeRequest.options.${option.value}`)}
                      </SelectItem>
                    ))}
                  </SelectSection>
                  <SelectSection
                    title={t("shipmentChangeRequest.senderSection")}
                  >
                    {FROM_OPTIONS.filter(
                      (option) =>
                        option.value !== "PICKUP_RESCHEDULE" ||
                        canReschedulePickup
                    ).map((option) => (
                      <SelectItem
                        key={option.value}
                        description={
                          option.value === "PICKUP_RESCHEDULE"
                            ? t("shipmentChangeRequest.descriptions.pickupMove")
                            : willBeAutoApproved([option.value])
                              ? t(
                                  "shipmentChangeRequest.descriptions.autoApproved"
                                )
                              : t(
                                  "shipmentChangeRequest.descriptions.requiresReview"
                                )
                        }
                      >
                        {t(`shipmentChangeRequest.options.${option.value}`)}
                      </SelectItem>
                    ))}
                  </SelectSection>
                </Select>

                {/* Dynamic Fields */}
                {selectedType && (
                  <div className="space-y-4 pt-4 border-t border-default-200">
                    {showInstructionsField && (
                      <div className="space-y-1">
                        <Textarea
                          label={
                            selectedType === "SENDER_PICKUP_INSTRUCTIONS"
                              ? t("shipmentChangeRequest.pickupInstructions")
                              : t("shipmentChangeRequest.deliveryInstructions")
                          }
                          placeholder={
                            selectedType === "SENDER_PICKUP_INSTRUCTIONS"
                              ? t("shipmentChangeRequest.pickupPlaceholder")
                              : t("shipmentChangeRequest.deliveryPlaceholder")
                          }
                          variant="flat"
                          value={formData.instructions}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              instructions: v,
                            }))
                          }
                        />
                        {originalValues?.instructions &&
                          formData.instructions ===
                            originalValues.instructions && (
                            <p className="text-xs text-default-500">
                              {t("shipmentChangeRequest.noChangesMade")}
                            </p>
                          )}
                        {selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" && (
                          <p className="text-xs text-warning-700">
                            {t("shipmentChangeRequest.existingLabelNotice")}
                          </p>
                        )}
                      </div>
                    )}

                    {showContactFields && (
                      <div className="space-y-3">
                        <p className="text-tiny font-medium text-default-700">
                          {t("shipmentChangeRequest.contactInformation")}
                        </p>
                        <Input
                          label={t("shipmentChangeRequest.name")}
                          placeholder={t("shipmentChangeRequest.contactName")}
                          variant="flat"
                          value={formData.contactName}
                          onValueChange={(v) =>
                            setFormData((prev) => ({ ...prev, contactName: v }))
                          }
                        />
                        <Input
                          label={t("shipmentChangeRequest.phone")}
                          placeholder={t("shipmentChangeRequest.phoneNumber")}
                          variant="flat"
                          value={formData.contactPhone}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              contactPhone: v,
                            }))
                          }
                        />
                        <Input
                          label={t("shipmentChangeRequest.email")}
                          placeholder={t("shipmentChangeRequest.emailAddress")}
                          type="email"
                          variant="flat"
                          value={formData.contactEmail}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              contactEmail: v,
                            }))
                          }
                        />
                      </div>
                    )}

                    {showAddressField && (
                      <div className="space-y-1">
                        <Input
                          label={t(
                            "shipmentChangeRequest.addressCorrectionLabel"
                          )}
                          placeholder={t(
                            "shipmentChangeRequest.addressCorrectionPlaceholder"
                          )}
                          variant="flat"
                          maxLength={100}
                          value={formData.addressLine2Correction}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              addressLine2Correction: v,
                            }))
                          }
                        />
                        <p className="text-xs text-default-500">
                          {t("shipmentChangeRequest.addressFeeNote")}
                        </p>
                        <p className="text-xs text-warning">
                          {t("shipmentChangeRequest.purchasedLabelWarning")}
                        </p>
                      </div>
                    )}

                    <Textarea
                      label={t("shipmentChangeRequest.additionalNotes")}
                      placeholder={t(
                        "shipmentChangeRequest.additionalNotesPlaceholder"
                      )}
                      variant="flat"
                      value={formData.additionalNotes}
                      onValueChange={(v) =>
                        setFormData((prev) => ({ ...prev, additionalNotes: v }))
                      }
                    />
                    {showAddressField &&
                      formData.additionalNotes.trim() &&
                      !hasChanges && (
                        <p className="text-xs text-warning">
                          {t("shipmentChangeRequest.notesDoNotSubmit")}
                        </p>
                      )}
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="flex-col sm:flex-row gap-2">
                {selectedType && (
                  <div className="flex-1 text-sm">
                    {isAutoApproved ? (
                      <span className="text-success flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        {t("shipmentChangeRequest.willApply")}
                      </span>
                    ) : (
                      <span className="text-warning flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {t("shipmentChangeRequest.requiresReview")}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="light" onPress={onClose}>
                    {t("actions.cancel")}
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleSubmit}
                    isDisabled={
                      !selectedType ||
                      !hasChanges ||
                      createChangeRequest.isPending ||
                      hasPendingRequests
                    }
                    isLoading={createChangeRequest.isPending}
                  >
                    <span>
                      {createChangeRequest.isPending
                        ? t("shipmentChangeRequest.submitting")
                        : !hasChanges && selectedType
                          ? t("shipmentChangeRequest.noChanges")
                          : t("shipmentChangeRequest.submit")}
                    </span>
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      {pickupMoveOpen && (
        <PickupRescheduleDialog
          open={pickupMoveOpen}
          onOpenChange={setPickupMoveOpen}
          bookingId={shipment.pickupBookingId ?? null}
          initialShipmentId={shipment.id}
        />
      )}
    </>
  );
}
